import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { useTranslation } from "react-i18next";
import YachtDetail from "./yachtAssetDetail";
import { getWhitelistAction } from "../../redux/booking/BookingSlice";
import { getUserDetail } from "../../redux/Auth/AuthSlice";
import "../../Asset/scss/takemyyacht.scss";

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#00305c",
    color: "#ffff",
    boxShadow: theme.shadows[10],
    fontSize: 14,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#00305c",
  },
}));

library.add(fas);
library.add(fab);

const YachtCards = ({
  id,
  headline,
  image,
  marina,
  province,
  travelAgent,
  travelAgentNo,
  vat,
  dayOption,
  destination,
  price,
  amenities,
  waterToy,
  cruising_speed,
  guests_day,
  guests_sleeping,
  style,
  onWhitelist,
}) => {
  const formattedPrice = price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const { t } = useTranslation();

  const todos = useSelector((state) => state);
  const dispatch = useDispatch();

  const token = useSelector((state) => state.auth?.token);
  const isAuth = useSelector((state) => state.auth?.isAuthenticated);
  const userDetails = useSelector((state) => state.AuthSlice.user);

  const [loading, setLoading] = useState(true);
  const [isWhitelisted, setIsWhitelisted] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (isAuth && token) {
        await dispatch(getUserDetail({ token }));
      }

      Promise.all([dispatch(getWhitelistAction({}))])
        .then(() => {
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
        });
    };

    fetchData();
  }, [dispatch, isAuth, token]);

  const checkWhitelist = () => {
    const userId = Number(userDetails?.idUserTMY);
    const yachtId = Number(id);

    const whitelist = todos.Booking.whitelistResults?.data;

    if (!Array.isArray(whitelist)) {
      return;
    }

    const isUserInWhitelist = whitelist.some(
      (item) => item.idUserTMY === userId
    );

    if (isUserInWhitelist && id) {
      const isYachtInWhitelist = whitelist.some(
        (item) =>
          Number(item.idUserTMY) === userId &&
          Number(item.id_sellingPrice) === yachtId
      );

      setIsWhitelisted(isYachtInWhitelist);
    } else {
    }
  };

  useEffect(() => {
    checkWhitelist();
  }, [
    todos.Booking.whitelistResults.data,
    userDetails?.idUserTMY,
    id,
    isWhitelisted,
  ]);

  const mapDay = (dayOption) => {
    switch (dayOption) {
      case "DayTrip":
        return t("Full Day");
      case "HalfDay":
        return t("Half Day");
      case "Overnight":
        return t("Overnight");
      default:
        return dayOption;
    }
  };

  const translatedDay = mapDay(dayOption);
  const destinationsArray = destination?.split(",");
  const truncatedDestinations = destinationsArray?.slice(0, 3).join(", ");
  const moreDestinations = destinationsArray?.length > 3 ? "..." : "";
  const fullDestination = destination;

  return (
    <Card sx={{ position: "relative" }}>
      {/* --- รูปด้านบนเต็มกว้าง --- */}
      <Box className="image-container-yacht-u">
        <img
          src={image}
          draggable={false}
          alt={headline}
          className="w-full"
        />

        <Box sx={{ position: "absolute", top: 8, left: 8 }}>
          <LightTooltip title={t("Add to Whitelist")} placement="right" arrow>
            <button
              className={`whitelist-button ${isWhitelisted ? "active" : ""}`}
              onClick={onWhitelist}
            >
              <FontAwesomeIcon icon={["fas", "heart"]} />
            </button>
          </LightTooltip>
        </Box>
      </Box>

      {/* --- ข้อมูลด้านล่าง แบ่งซ้าย/ขวา --- */}
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {/* ข้อมูลด้านซ้าย */}
        <Grid item xs={6} sm={6} md={6}>
          <CardContent className="card-body">
            <Typography className="headline-text">{headline}</Typography>
            <Typography className="destination1-text">
              <FontAwesomeIcon
                icon={["fas", "map-marker-alt"]}
                className="mr-2"
              />
              <span>
                {marina}, {province}
              </span>
            </Typography>
            <Typography variant="body2" className="destination2-text">
              <LightTooltip title={fullDestination} placement="bottom" arrow>
                <span>
                  {truncatedDestinations}
                  {moreDestinations}
                </span>
              </LightTooltip>
            </Typography>
            <Typography className="text-muted destination3-text">
              {t("Operated")}{" "}
              {travelAgent.length >= 20
                ? `${travelAgent.substring(0, 17)}...`
                : travelAgent}
              ,
              <br />
              {t("TA No.")}
              {travelAgentNo}
            </Typography>
            <h2 className="text-danger text-left day-text">{translatedDay}</h2>
            <div className="destination5-text">
              <Typography variant="body2" className="destination4-text">
                {t("Amenities-Water")}
              </Typography>
              <div>
                {amenities.map((amenity, index) => (
                  <LightTooltip
                    key={index}
                    title={amenity.name_amenities}
                    placement="bottom"
                    arrow
                  >
                    <FontAwesomeIcon
                      icon={amenity.icon_amenities.split(" ")}
                      className="mr-2 amenities-icon"
                    />
                  </LightTooltip>
                ))}
                {waterToy.map((waterToy, index) => (
                  <LightTooltip
                    key={index}
                    title={waterToy.name_waterToy}
                    placement="bottom"
                    arrow
                  >
                    <FontAwesomeIcon
                      icon={waterToy.icon_waterToy.split(" ")}
                      className="mr-2 amenities-icon"
                    />
                  </LightTooltip>
                ))}
              </div>
            </div>
          </CardContent>
        </Grid>

        {/* ข้อมูลด้านขวา */}
        <Grid item xs={6} sm={6} md={6}>
          <CardContent>
            <Box textAlign="right" sx={{ marginBottom: "1vh" }}>
              <YachtDetail
                cruisingSpeed={cruising_speed}
                guestDay={guests_day}
                guestSleeping={guests_sleeping}
              />
            </Box>
            <Box textAlign="right" className="price-container">
              <Typography className="start-from">{t("Start-from")}</Typography>
              <div className="price-align">
                <div className="price-row">
                  <Typography className="thb-text">THB</Typography>
                  <Typography className="text-success price-text">
                    {formattedPrice}
                  </Typography>
                </div>

                {vat ? (
                  <Tooltip title={t("This price is subject to VAT")}>
                    <Typography
                      className="start-from vat-text cursor-help"
                    >
                      *&nbsp;{t("This price is subject to VAT")}
                    </Typography>
                  </Tooltip>
                ) : null}
              </div>
            </Box>
          </CardContent>
        </Grid>
      </Grid>
    </Card>
  );
};

export default YachtCards;
